import * as L from 'leaflet';

/* Copied and translated from 
https://github.com/QuantStack/leaflet-splitmap/blob/master/src/index.js
*/

interface SplitMapOptions extends L.ControlOptions {
    thumbSize: number;
    padding: number;
}

let mapWasDragEnabled: boolean;
let mapWasTapEnabled: boolean | undefined;

// Leaflet v0.7 backwards compatibility
function on(el: HTMLElement, types: string, fn: (e: Event) => void, context: any): void {
    types.split(' ').forEach((type) => {
        L.DomEvent.on(el, type, fn, context);
    });
}

// Leaflet v0.7 backwards compatibility
function off(el: HTMLElement, types: string, fn: (e: Event) => void, context: any): void {
    types.split(' ').forEach((type) => {
        L.DomEvent.off(el, type, fn, context);
    });
}

function getRangeEvent(rangeInput: HTMLInputElement): string {
    return 'oninput' in rangeInput ? 'input' : 'change';
}

function cancelMapDrag(this: SplitMap): void {
    mapWasDragEnabled = this._map!.dragging.enabled();
    mapWasTapEnabled = (this._map as any).tap && (this._map as any).tap.enabled();
    this._map!.dragging.disable();
    if ((this._map as any).tap) {
        (this._map as any).tap.disable();
    }
}

function uncancelMapDrag(this: SplitMap, e: Event): void {
    this._refocusOnMap(e);
    if (!this._map) {
        return;
    }

    if (mapWasDragEnabled) {
        this._map.dragging.enable();
    }

    if (mapWasTapEnabled && (this._map as any).tap) {
        (this._map as any).tap.enable();
    }
}

// convert arg to an array - returns empty array if arg is null/undefined
function asArray<T>(arg: T | T[] | undefined | null): T[] {
    if (arg == null) {
        return [];
    }
    const values = Array.isArray(arg) ? arg : [arg];
    return values.filter((value): value is T => value != null);
}


export interface DividerMoveEvent extends L.LeafletEvent {
    x: number;
}


export class SplitMap extends L.Control {
    override options: SplitMapOptions;
    _leftLayers: L.Layer[];
    _rightLayers: L.Layer[];
    _range: any;
    _container: any;
    _divider: any;
    _map: L.Map | null;

    constructor(leftLayers: L.Layer | L.Layer[], rightLayers: L.Layer | L.Layer[], options?: Partial<SplitMapOptions>) {
        super(options);
        this.options = { ...{ thumbSize: 42, padding: 0 }, ...options };
        this._leftLayers = asArray(leftLayers);
        this._rightLayers = asArray(rightLayers);
        this._updateClip();
    }

    getPosition2(): number {
        if (!this._map || !this._range) {
            return 0;
        }

        const rangeValue = parseFloat(this._range.value);
        const offset = (0.5 - rangeValue) * (2 * this.options.padding + this.options.thumbSize);
        return this._map.getSize().x * rangeValue + offset;
    }

    private _getLayerPane(layer: any): HTMLElement | null {
        if (!layer || !layer._map || typeof layer.getPane !== 'function') {
            return null;
        }

        try {
            return layer.getPane();
        } catch {
            return null;
        }
    }



    override setPosition(position) {
        return this
    }

    override addTo(map: L.Map): this {
        this.remove();
        this._map = map;
        const container = this._container = L.DomUtil.create('div', 'leaflet-sbs', map.getContainer());
        this._divider = L.DomUtil.create('div', 'leaflet-sbs-divider', container);
        const range: any = this._range = L.DomUtil.create('input', 'leaflet-sbs-range', container);
        range.type = 'range';
        range.min = '0';
        range.max = '1';
        range.step = 'any';
        range.value = '0.5';
        range.style.paddingLeft = range.style.paddingRight = this.options.padding + 'px';
        this._addEvents();
        this._updateClip();
        return this;
    }

    override remove(): this {
        if (!this._map) {
            return this;
        }
        this._removeEvents();
        this._leftLayers.forEach((left_layer: any) => {
            const pane = this._getLayerPane(left_layer);
            if (pane) {
                pane.style.clip = '';
            }
        });

        this._rightLayers.forEach((right_layer: any) => {
            const pane = this._getLayerPane(right_layer);
            if (pane) {
                pane.style.clip = '';
            }
        });
        if (this._container) {
            L.DomUtil.remove(this._container);
        }
        this._leftLayers = [];
        this._rightLayers = [];
        this._container = null;
        this._divider = null;
        this._range = null;
        this._map = null;
        return this;
    }

    _updateClip(): this {
        if (!this._map || !this._divider || !this._range) {
            return this;
        }
        const map = this._map;
        const nw = map.containerPointToLayerPoint([0, 0]);
        const se = map.containerPointToLayerPoint(map.getSize());
        const clipX = nw.x + this.getPosition2();
        const dividerX = this.getPosition2();
        this._divider.style.left = dividerX + 'px';
        this.fire('dividermove', { x: dividerX } as DividerMoveEvent);
        const clipLeft = `rect(${nw.y}px,${clipX}px,${se.y}px,${nw.x}px)`;
        const clipRight = `rect(${nw.y}px,${se.x}px,${se.y}px,${clipX}px)`;

        this._leftLayers = asArray(this._leftLayers).filter(
            (leftLayer: any) => !!this._getLayerPane(leftLayer)
        );
        this._rightLayers = asArray(this._rightLayers).filter(
            (rightLayer: any) => !!this._getLayerPane(rightLayer)
        );

        this._leftLayers.forEach((left_layer: any) => {
            const pane = this._getLayerPane(left_layer);
            if (pane) {
                pane.style.clip = clipLeft;
            }
        });

        this._rightLayers.forEach((right_layer: any) => {
            const pane = this._getLayerPane(right_layer);
            if (pane) {
                pane.style.clip = clipRight;
            }
        });
        return this;
    }

    _addEvents(): void {
        const range = this._range;
        const map = this._map;
        if (!map || !range) return;
        map.on('move', this._updateClip, this);
        map.on('layeradd layerremove', this._updateLayers, this);
        on(range, getRangeEvent(range), this._updateClip, this);
        on(range, 'ontouchstart' in window ? 'touchstart' : 'mousedown', cancelMapDrag, this);
        on(range, 'ontouchend' in window ? 'touchend' : 'mouseup', uncancelMapDrag, this);
    }

    _removeEvents(): void {
        const range = this._range;
        const map = this._map;
        if (range) {
            off(range, getRangeEvent(range), this._updateClip, this);
            off(range, 'ontouchstart' in window ? 'touchstart' : 'mousedown', cancelMapDrag, this);
            off(range, 'ontouchend' in window ? 'touchend' : 'mouseup', uncancelMapDrag, this);
        }
        if (map) {
            map.off('layeradd layerremove', this._updateLayers, this);
            map.off('move', this._updateClip, this);
        }
    }

    _updateLayers(): void {
        // This method is not implemented in the original code
    }

    _refocusOnMap(e: Event): void {
        // This method is not implemented in the original code
    }
}

Object.assign(SplitMap.prototype, L.Evented.prototype);

// Extend the L.Control namespace
declare module 'leaflet' {
    interface Control {
        fire(type: string, data?: any): this;
        on(type: string, fn: L.LeafletEventHandlerFn, context?: any): this;
        off(type: string, fn?: L.LeafletEventHandlerFn, context?: any): this;
    }
}
